import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { EmptyState } from '@/components/common/EmptyState';

const useStyles = makeStyles({
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/**
 * Catch-all for errors thrown during routing or rendering.
 *
 * The chunk-load case is worth special handling: after a deploy, the hashed
 * chunks a long-open tab remembers no longer exist, so a lazy route fails to
 * import. Reloading genuinely is the fix, so offer it explicitly rather than a
 * generic "try again" that will fail the same way.
 */
export function RouteErrorPage() {
  const styles = useStyles();
  const error = useRouteError();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred.';

  const isStaleChunk =
    error instanceof Error &&
    /Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      error.message,
    );

  return (
    <div className={styles.page}>
      {isStaleChunk ? (
        <EmptyState
          title="A new version is available"
          description="Reload the page to pick up the latest version of the portal."
          action={
            <Button appearance="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      ) : (
        <EmptyState
          title="Something went wrong"
          description={message}
          action={
            <Button appearance="primary" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          }
        />
      )}
    </div>
  );
}

export default RouteErrorPage;
